import { REALM_MOBS, AFFIX_IDS, MOBS } from '../content/mobs';
import { dist } from '../math';
import type { World } from './world';
import type { AnyUnit, GhostSnapshot, Interactable, MobEnt, PlayerEnt } from './types';
import { spawnMob, spawnRevenant } from './mobs';
import type { Point } from './map';

export type RealmPhase = 'open' | 'boss' | 'closing' | 'closed';

export interface RealmState {
  code: string;
  bossDef: string;
  corruption: number;
  corruptionNeeded: number;
  phase: RealmPhase;
  bossId: number;
  closeAt: number;
  kills: number;
  startTick: number;
  /** revenant pool supplied by the room */
  ghosts: GhostSnapshot[];
  ghostCursor: number;
  ghostTimer: number;
  /** ghost ids that must spawn when their avenger arrives */
  priorityGhosts: GhostSnapshot[];
  bossKilledBy: string;
}

export function initRealm(world: World, code: string, bossDef: string, ghosts: GhostSnapshot[]): RealmState {
  const st: RealmState = {
    code,
    bossDef,
    corruption: 0,
    corruptionNeeded: 140,
    phase: 'open',
    bossId: 0,
    closeAt: 0,
    kills: 0,
    startTick: world.tick,
    ghosts,
    ghostCursor: 0,
    ghostTimer: world.seconds(35),
    priorityGhosts: [],
    bossKilledBy: '',
  };
  world.realm = st;
  placeInteractables(world);
  return st;
}

function placeInteractables(world: World): void {
  const map = world.map;
  const rng = world.rng.fork('interactables');
  const placed: Point[] = [];
  const count = 26;
  let tries = 0;
  while (placed.length < count && tries++ < 600) {
    const x = rng.float(120, map.widthPx - 120);
    const y = rng.float(120, map.heightPx - 120);
    if (!map.clear(x, y, 20)) continue;
    if (dist(x, y, map.center.x, map.center.y) < map.arenaRadius + 200) continue;
    if (placed.some((p) => dist(p.x, p.y, x, y) < 380)) continue;
    placed.push({ x, y });
    const roll = rng.next();
    const type: Interactable['type'] = roll < 0.45 ? 'shrine' : roll < 0.8 ? 'chest' : 'altar';
    const id = world.allocId();
    world.interactables.set(id, { id, x, y, r: 18, type, used: false, tier: map.tierAt(x, y), seed: rng.int(1, 1e9) });
  }
}

export function realmSpawnPoint(world: World, index?: number): Point {
  const pts = world.map.spawnPoints;
  if (!pts.length) return world.map.center;
  const i = index !== undefined ? index % pts.length : world.rng.int(0, pts.length - 1);
  return pts[i];
}

export function addCorruption(world: World, amount: number): void {
  const st = world.realm;
  if (!st || st.phase !== 'open') return;
  st.kills++;
  st.corruption += amount;
}

function alivePlayers(world: World): PlayerEnt[] {
  const out: PlayerEnt[] = [];
  for (const p of world.players.values()) if (!p.dead) out.push(p);
  return out;
}

export function stepRealm(world: World): void {
  const st = world.realm!;
  if (st.phase === 'closed') return;
  const players = alivePlayers(world);
  st.corruptionNeeded = 140 + 80 * Math.max(1, world.players.size);
  if (st.phase === 'open') {
    // slow trickle guarantees a boss eventually (~15 min)
    if (world.tick % 20 === 0 && players.length) st.corruption += st.corruptionNeeded / 900;
    if (st.corruption >= st.corruptionNeeded && players.length) spawnBoss(world);
  }
  if (world.tick % 20 === 0) spawnWave(world, players);
  if (st.phase !== 'closing' && --st.ghostTimer <= 0) {
    st.ghostTimer = world.seconds(world.rng.float(45, 75));
    spawnGhost(world, players);
  }
  if (st.phase === 'closing' && world.tick >= st.closeAt) st.phase = 'closed';
}

export function spawnBoss(world: World): void {
  const st = world.realm!;
  const c = world.map.center;
  const n = Math.max(1, world.players.size);
  const boss = spawnMob(world, st.bossDef, c.x, c.y, { hpScale: 1 + 0.55 * (n - 1), dmgScale: 1 + 0.08 * (n - 1) });
  st.bossId = boss.id;
  st.phase = 'boss';
  world.events.push({ t: 'announce', key: 'announce.bossArrives', vars: { boss: st.bossDef }, color: '#ff6a3c' });
  world.events.push({ t: 'shake', id: boss.id, power: 2 });
}

export function onBossKilledRealm(world: World, boss: MobEnt, killer: AnyUnit | null): void {
  const st = world.realm;
  if (!st) return;
  st.phase = 'closing';
  st.closeAt = world.tick + world.seconds(30);
  st.bossKilledBy = killer && killer.kind === 'player' ? (killer as PlayerEnt).name : '';
  for (const p of world.players.values()) {
    if (p.dead) continue;
    p.fameEarned += 60 + p.level * 6;
  }
  world.events.push({ t: 'announce', key: 'announce.bossSlain', vars: { boss: boss.def, killer: st.bossKilledBy }, color: '#ffd166' });
}

function countMobsNear(world: World, x: number, y: number, r: number): number {
  let n = 0;
  for (const u of world.hash.queryCircle(x, y, r)) {
    const k = (u as AnyUnit).kind;
    if (k === 'mob' || k === 'boss' || k === 'revenant') n++;
  }
  return n;
}

function spawnWave(world: World, players: PlayerEnt[]): void {
  if (!players.length) return;
  let total = 0;
  for (const m of world.mobs.values()) if (!m.dead && m.kind !== 'summon') total++;
  const globalCap = 45 + 12 * players.length;
  for (const p of players) {
    if (total >= globalCap) break;
    const tier = world.map.tierAt(p.x, p.y);
    const others = players.filter((o) => o !== p && dist(o.x, o.y, p.x, p.y) < 700).length;
    const cap = (tier === 1 ? 8 : tier === 2 ? 10 : 12) + others * 3;
    const near = countMobsNear(world, p.x, p.y, 1000);
    if (near >= cap) continue;
    const pos = world.map.randomPointNear(world.rng, p.x, p.y, 640, 980, 16);
    if (!pos) continue;
    if (players.some((o) => o !== p && dist(o.x, o.y, pos.x, pos.y) < 420)) continue;
    let t = world.map.tierAt(pos.x, pos.y);
    let cands = REALM_MOBS.filter((d) => d.tier === t);
    if (!cands.length) {
      t = Math.max(1, t - 1);
      cands = REALM_MOBS.filter((d) => d.tier === t);
    }
    if (!cands.length) continue;
    const def = cands[world.rng.weightedIndex(cands.map((d) => d.weight ?? 1))];
    const pack = def.pack ?? 1;
    const nearPlayers = players.filter((o) => dist(o.x, o.y, pos.x, pos.y) < 900).length;
    const hpScale = 1 + 0.35 * Math.max(0, nearPlayers - 1);
    const eliteChance = 0.05 + 0.03 * t;
    const elite = world.rng.chance(eliteChance) && pack <= 2;
    const affixes: string[] = [];
    if (elite) {
      affixes.push(world.rng.pick(AFFIX_IDS));
      if (t === 3 && world.rng.chance(0.4)) {
        const second = world.rng.pick(AFFIX_IDS);
        if (!affixes.includes(second)) affixes.push(second);
      }
    }
    for (let i = 0; i < pack; i++) {
      const a = (i / pack) * Math.PI * 2;
      spawnMob(world, def.id, pos.x + Math.cos(a) * 26 * (pack > 1 ? 1 : 0), pos.y + Math.sin(a) * 26 * (pack > 1 ? 1 : 0), { elite: elite && i === 0, affixes: i === 0 ? affixes : [], hpScale });
      total++;
    }
  }
}

function spawnGhost(world: World, players: PlayerEnt[]): void {
  const st = world.realm!;
  if (!players.length) return;
  let ghost: GhostSnapshot | undefined = st.priorityGhosts.shift();
  if (!ghost) {
    if (!st.ghosts.length) return;
    ghost = st.ghosts[st.ghostCursor % st.ghosts.length];
    st.ghostCursor++;
  }
  // never spawn a player's own ghost while they are in the realm
  const target = players.filter((p) => p.accountId !== ghost!.accountId);
  const p = target.length ? world.rng.pick(target) : players[0];
  spawnRevenantNear(world, ghost, p);
}

export function spawnRevenantNear(world: World, ghost: GhostSnapshot, p: PlayerEnt): MobEnt | null {
  const pos = world.map.randomPointNear(world.rng, p.x, p.y, 520, 760, 16);
  if (!pos) return null;
  const tier = world.map.tierAt(pos.x, pos.y);
  const m = spawnRevenant(world, ghost, pos.x, pos.y, tier);
  world.events.push({ t: 'feed', key: 'feed.revenantAppears', vars: { name: ghost.name } });
  return m;
}

export function bossName(id: string): string {
  return MOBS[id]?.id ?? id;
}
